import { db } from "../firebase/config";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { loadStripe } from '@stripe/stripe-js';
import { sendReceiptNotification } from "./emailService";
import getReceiptUrl from "./receiptService";

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/**
 * Creates a checkout session and redirects to Stripe Checkout
 * @param {string} orderId - Reference to the order in Firestore
 * @param {string} priceId - Stripe Price ID for the product
 * @param {string} userId - The user ID from Firebase Auth
 * @param {number} quantity - Number of items to purchase
 * @param {string} email - User's email address to prefill in Stripe checkout
 * @param {boolean} isGuest - Whether the user is a guest or authenticated
 * @returns {Promise<{success: boolean, error: string}>}
 */
export const createCheckoutSession = async (orderId, priceId, userId, quantity = 1, email = null, isGuest = false) => {
  try {
    // Get Stripe instance
    const stripe = await stripePromise;
    if (!stripe) {
      throw new Error('Stripe failed to initialize');
    }

    // Set the success URL based on whether it's a guest or authenticated user
    const successUrl = isGuest 
      ? `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&success=true&order_id=${orderId}`
      : `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&success=true&order_id=${orderId}`;

    // Create checkout options
    const checkoutOptions = {
      lineItems: [
        {
          price: priceId,
          quantity: quantity
        }
      ],
      mode: 'payment',
      successUrl: successUrl,
      cancelUrl: `${window.location.origin}/packages?canceled=true`,
      clientReferenceId: userId
    };
    
    // Add customer email if available
    if (email) {
      checkoutOptions.customerEmail = email;
    }

    // Create a checkout session using Stripe Checkout
    const { error } = await stripe.redirectToCheckout(checkoutOptions);

    if (error) {
      console.error('Stripe checkout error:', error);
      return {
        success: false,
        error: error.message
      };
    }

    return {
      success: true
    };
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Handles the success redirect from Stripe Checkout
 * @param {string} sessionId - The Stripe session ID from the URL
 * @param {string} orderId - The order ID from the URL
 * @param {Object} userData - The current user data
 * @returns {Promise<{success: boolean}>}
 */
export const handleCheckoutSuccess = async (sessionId, orderId, userData) => {
  try {
    // Try to get receipt URL from Stripe
    let receiptUrl = null;
    try {
      receiptUrl = await getReceiptUrl(sessionId);
      console.log('Receipt URL fetched:', receiptUrl ? 'Successfully retrieved' : 'Not available');
    } catch (error) {
      console.error('Error getting receipt URL:', error);
      // Continue even if receipt URL fetch fails
    }
    
    // Fetch the current order data
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }
    
    const orderData = orderSnap.data();
    
    // Prepare update data
    const updateData = {
      status: 'completed',
      paidAt: new Date().toISOString(),
      sessionId: sessionId // Store the session ID for future use
    };
    
    // Add receipt URL if available
    if (receiptUrl) {
      updateData.receipt_url = receiptUrl;
    }
    
    // Update order status to completed in Firestore
    await updateDoc(orderRef, updateData);
    
    // Send receipt email if we have all necessary data
    if (receiptUrl && userData && userData.email) {
      try {
        await sendReceiptNotification({
          userEmail: userData.email,
          userName: userData.name || userData.email.split('@')[0],
          orderNumber: orderId,
          packageName: orderData.packageTitle,
          amount: orderData.totalPrice,
          receiptUrl: receiptUrl
        });
        console.log('Receipt notification email sent successfully');
      } catch (emailError) {
        console.error('Error sending receipt notification email:', emailError);
        // Continue even if email sending fails
      }
    }
    
    return {
      success: true,
      receiptUrl
    };
  } catch (error) {
    console.error('Error handling checkout success:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
